/**
 * Permission Management Page - AWLP Design System
 * Read-only view of all permissions grouped by resource
 */

import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import styled from 'styled-components';
import { Search, ChevronDown, ChevronUp, Key } from 'lucide-react';
import { Input } from '../../components/ui/Input';
import { Card } from '../../components/ui/Card';
import permissionService, { Permission } from '../../services/api/permissionService';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[6]};
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing[4]};

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const TitleSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[2]};
`;

const Title = styled.h1`
  font-size: ${({ theme }) => theme.typography.fontSize['3xl']};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const Subtitle = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const SearchContainer = styled.div`
  flex: 1;
  max-width: 400px;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    max-width: 100%;
  }
`;

const ResourceList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[4]};
`;

const ResourceCard = styled(Card)`
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.base};
`;

const ResourceHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[3]};
  padding: ${({ theme }) => theme.spacing[4]};
`;

const ResourceIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background: ${({ theme }) => theme.colors.primary.lightSky}20;
  color: ${({ theme }) => theme.colors.primary.ocean};

  svg {
    width: 20px;
    height: 20px;
  }
`;

const ResourceInfo = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[1]};
`;

const ResourceName = styled.h3`
  font-size: ${({ theme }) => theme.typography.fontSize.lg};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const ResourceCount = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const ExpandIcon = styled.div`
  color: ${({ theme }) => theme.colors.text.secondary};
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 20px;
    height: 20px;
  }
`;

const ResourceBody = styled.div<{ $isExpanded: boolean }>`
  display: ${({ $isExpanded }) => ($isExpanded ? 'block' : 'none')};
  padding: 0 ${({ theme }) => theme.spacing[4]} ${({ theme }) => theme.spacing[4]};
`;

const PermissionGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: ${({ theme }) => theme.spacing[3]};
`;

const PermissionCard = styled.div`
  padding: ${({ theme }) => theme.spacing[3]};
  background: ${({ theme }) => theme.colors.backgrounds.gray};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  border: 1px solid ${({ theme }) => theme.colors.borders.light};
`;

const PermissionKey = styled.div`
  font-family: 'Monaco', 'Courier New', monospace;
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.colors.primary.ocean};
  background: ${({ theme }) => theme.colors.primary.lightSky}20;
  padding: ${({ theme }) => theme.spacing[1]} ${({ theme }) => theme.spacing[2]};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  margin-bottom: ${({ theme }) => theme.spacing[2]};
  word-break: break-all;
`;

const PermissionDisplayName = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: ${({ theme }) => theme.spacing[1]};
`;

const PermissionDescription = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.colors.text.secondary};
  line-height: 1.5;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${({ theme }) => theme.spacing[12]} ${({ theme }) => theme.spacing[4]};
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const LoadingState = styled.div`
  text-align: center;
  padding: ${({ theme }) => theme.spacing[12]} ${({ theme }) => theme.spacing[4]};
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const StatsBar = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing[6]};
  padding: ${({ theme }) => theme.spacing[4]};
  background: ${({ theme }) => theme.colors.backgrounds.gray};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
`;

const StatItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[1]};
`;

const StatValue = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize['2xl']};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  color: ${({ theme }) => theme.colors.primary.ocean};
`;

const StatLabel = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.colors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

export const PermissionManagementPage = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedResources, setExpandedResources] = useState<Set<string>>(new Set());

  const { data: permissionsData, isLoading } = useQuery({
    queryKey: ['permissions'],
    queryFn: () => permissionService.getAllPermissions(),
  });

  // Calculate total permissions and resources
  const stats = useMemo(() => {
    if (!permissionsData) return { totalPermissions: 0, totalResources: 0 };

    const totalPermissions = Object.values(permissionsData).reduce(
      (sum, permissions) => sum + permissions.length,
      0,
    );
    const totalResources = Object.keys(permissionsData).length;

    return { totalPermissions, totalResources };
  }, [permissionsData]);

  // Filter permissions by search query
  const filteredPermissions = useMemo(() => {
    if (!permissionsData) return {};
    if (!searchQuery) return permissionsData;

    const searchLower = searchQuery.toLowerCase();
    const filtered: Record<string, Permission[]> = {};

    Object.entries(permissionsData).forEach(([resource, permissions]) => {
      const matchingPerms = permissions.filter(
        (perm) =>
          perm.displayName.toLowerCase().includes(searchLower) ||
          perm.key.toLowerCase().includes(searchLower) ||
          perm.description?.toLowerCase().includes(searchLower) ||
          resource.toLowerCase().includes(searchLower),
      );

      if (matchingPerms.length > 0) {
        filtered[resource] = matchingPerms;
      }
    });

    return filtered;
  }, [permissionsData, searchQuery]);

  const toggleResource = (resource: string) => {
    const newExpanded = new Set(expandedResources);
    if (newExpanded.has(resource)) {
      newExpanded.delete(resource);
    } else {
      newExpanded.add(resource);
    }
    setExpandedResources(newExpanded);
  };

  if (isLoading) {
    return (
      <Container>
        <Header>
          <Title>Permissions</Title>
        </Header>
        <LoadingState>Loading permissions...</LoadingState>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <TitleSection>
          <Title>Permissions</Title>
          <Subtitle>Browse all available permissions in the system grouped by resource</Subtitle>
        </TitleSection>
        <SearchContainer>
          <Input
            type="search"
            placeholder="Search permissions..."
            leftIcon={<Search />}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            fullWidth
          />
        </SearchContainer>
      </Header>

      <StatsBar>
        <StatItem>
          <StatValue>{stats.totalPermissions}</StatValue>
          <StatLabel>Total Permissions</StatLabel>
        </StatItem>
        <StatItem>
          <StatValue>{stats.totalResources}</StatValue>
          <StatLabel>Resources</StatLabel>
        </StatItem>
      </StatsBar>

      {Object.keys(filteredPermissions).length === 0 ? (
        <EmptyState>No permissions found</EmptyState>
      ) : (
        <ResourceList>
          {Object.entries(filteredPermissions).map(([resource, permissions]) => {
            const isExpanded = expandedResources.has(resource);

            return (
              <ResourceCard key={resource} padding="none">
                <ResourceHeader onClick={() => toggleResource(resource)}>
                  <ResourceIcon>
                    <Key />
                  </ResourceIcon>
                  <ResourceInfo>
                    <ResourceName>{resource}</ResourceName>
                    <ResourceCount>
                      {permissions.length} permission{permissions.length !== 1 ? 's' : ''}
                    </ResourceCount>
                  </ResourceInfo>
                  <ExpandIcon>{isExpanded ? <ChevronUp /> : <ChevronDown />}</ExpandIcon>
                </ResourceHeader>

                <ResourceBody $isExpanded={isExpanded}>
                  <PermissionGrid>
                    {permissions.map((permission) => (
                      <PermissionCard key={permission.id}>
                        <PermissionKey>{permission.key}</PermissionKey>
                        <PermissionDisplayName>{permission.displayName}</PermissionDisplayName>
                        {permission.description && (
                          <PermissionDescription>{permission.description}</PermissionDescription>
                        )}
                      </PermissionCard>
                    ))}
                  </PermissionGrid>
                </ResourceBody>
              </ResourceCard>
            );
          })}
        </ResourceList>
      )}
    </Container>
  );
};

export default PermissionManagementPage;
